import { useMemo } from "react";
import { useAvailableGroups } from "@/hooks/use-users";
import type { KeycloakGroup } from "@/api/users";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@fox1ck-org/ui";

interface KeycloakGroupPickerProps {
  value: string | null;
  onSelect: (group: { id: string; name: string; path: string } | null) => void;
  placeholder?: string;
}

interface FlatGroup {
  id: string;
  name: string;
  path: string;
  depth: number;
}

function flattenGroups(groups: KeycloakGroup[], depth = 0): FlatGroup[] {
  const result: FlatGroup[] = [];
  for (const group of groups) {
    result.push({ id: group.id, name: group.name, path: group.path, depth });
    if (group.subGroups?.length) {
      result.push(...flattenGroups(group.subGroups, depth + 1));
    }
  }
  return result;
}

export function KeycloakGroupPicker({
  value,
  onSelect,
  placeholder = "Выберите группу Keycloak…",
}: KeycloakGroupPickerProps) {
  const { data } = useAvailableGroups();

  const flatGroups = useMemo(() => flattenGroups(data?.groups ?? []), [data]);

  return (
    <Select
      value={value ?? "__none"}
      onValueChange={(v) => {
        if (v === "__none") {
          onSelect(null);
          return;
        }
        const group = flatGroups.find((g) => g.id === v);
        if (group) onSelect({ id: group.id, name: group.name, path: group.path });
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">
          <span className="text-muted-foreground">Нет</span>
        </SelectItem>
        {flatGroups.map((group) => (
          <SelectItem key={group.id} value={String(group.id)}>
            <span style={{ paddingLeft: `${group.depth * 12}px` }}>
              {group.name}
              <span className="text-xs text-muted-foreground ml-2 font-mono">
                {group.path}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
